import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import moment from 'moment';
import CustomButton from '../../components/common/Button';
import { Switch } from './components/ui/Switch';
import { RuleCard } from './components/ui/RuleCard';
import { TestRuleDialog } from './components/modals/TestRuleDialog';
import StatisticsCard from '../../components/StatisticsCard';
import AllocationFilters from './components/ui/AllocationFilters';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { IconButton, Menu, MenuItem, Tooltip, Skeleton, FormControlLabel, Switch as MUISwitch } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as VisibilityIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as RuleIcon,
} from '@mui/icons-material';
import { BadgeVariant } from './components/ui/Badge';
import { Badge } from './components/ui/Badge';
import { stagingOptions } from '../../constants/apollo';
import CircularProgress from '@mui/material/CircularProgress';
import {
  FETCH_ALLOCATION_RULES,
  FETCH_ALLOCATION_ENGINE_STATUS,
  TOGGLE_ALLOCATION_ENGINE,
  DELETE_ALLOCATION_RULE,
  UPDATE_ALLOCATION_RULE,
} from '../../api/queries/allocation';

const DEFAULT_FILTERS = {
  status: 'all',
  resultVia: 'all',
  priority: 'all',
  searchText: '',
};

const shouldShowTooltip = (text: string | undefined | null): boolean => {
  return Boolean(text && text.trim().length > 0);
};

const truncateToastText = (text: string | undefined | null, maxLength: number = 12): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const VALID_STATUSES = new Set(['all', 'active', 'inactive']);
const VALID_PRIORITIES = new Set(['all', 'high', 'medium', 'low']);
const VIEW_MODES = new Set(['card', 'table']);
const DEFAULT_VIEW_MODE = 'card';

const STORAGE_KEYS = {
  page: 'allocation:page',
  filters: 'allocation:filters',
  viewMode: 'allocation:view',
};

const isBrowser = typeof window !== 'undefined';

const storage = {
  get: (key: string): string | null => {
    if (!isBrowser) return null;
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.warn(`Allocation storage read failed for ${key}`, error);
      return null;
    }
  },
  set: (key: string, value: string): void => {
    if (!isBrowser) return;
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      console.warn(`Allocation storage write failed for ${key}`, error);
    }
  },
};

const getStoredPage = () => {
  const raw = storage.get(STORAGE_KEYS.page);
  if (raw === null) return null;
  const parsed = Number(raw);
  if (Number.isNaN(parsed) || parsed < 0) return null;
  return Math.floor(parsed);
};

const getStoredViewMode = () => {
  const raw = storage.get(STORAGE_KEYS.viewMode);
  return raw && VIEW_MODES.has(raw) ? raw : null;
};

const getStoredFilters = () => {
  const raw = storage.get(STORAGE_KEYS.filters);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_FILTERS,
      ...parsed,
    };
  } catch (error) {
    console.warn('Allocation filters storage parse failed', error);
    return null;
  }
};

const getPageFromParams = (params: URLSearchParams | null) => {
  if (!params) {
    return { page: 0, hasOverride: false };
  }

  const raw = params.get('page');
  if (!raw) {
    return { page: 0, hasOverride: false };
  }

  const parsed = Number(raw);
  if (Number.isNaN(parsed) || parsed < 1) {
    return { page: 0, hasOverride: true };
  }

  return { page: Math.max(0, Math.floor(parsed) - 1), hasOverride: true };
};

const getFiltersFromParams = (params: URLSearchParams | null) => {
  const nextFilters = { ...DEFAULT_FILTERS };
  let hasOverrides = false;

  if (params) {
    const statusParam = params.get('status');
    if (statusParam && VALID_STATUSES.has(statusParam)) {
      nextFilters.status = statusParam;
      hasOverrides = true;
    }

    const resultViaParam = params.get('resultVia');
    if (resultViaParam) {
      nextFilters.resultVia = resultViaParam;
      hasOverrides = true;
    }

    const priorityParam = params.get('priority');
    if (priorityParam && VALID_PRIORITIES.has(priorityParam)) {
      nextFilters.priority = priorityParam;
      hasOverrides = true;
    }

    if (params.has('searchText')) {
      nextFilters.searchText = params.get('searchText') || '';
      hasOverrides = true;
    }
  }

  return { filters: nextFilters, hasOverrides };
};

const getViewModeFromParams = (params: URLSearchParams | null) => {
  if (!params) {
    return { viewMode: DEFAULT_VIEW_MODE, hasOverride: false };
  }
  const viewParam = params.get('view');
  if (viewParam && VIEW_MODES.has(viewParam)) {
    return { viewMode: viewParam, hasOverride: true };
  }
  return { viewMode: DEFAULT_VIEW_MODE, hasOverride: false };
};

const areFiltersEqual = (a: any, b: any): boolean =>
  a.status === b.status &&
  a.resultVia === b.resultVia &&
  a.priority === b.priority &&
  a.searchText === b.searchText;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastSyncedSearchParamsRef = useRef(searchParams.toString());

  const pageFromParamsInfo = getPageFromParams(searchParams);
  const [page, setPage] = useState(() => {
    if (pageFromParamsInfo.hasOverride) {
      return pageFromParamsInfo.page;
    }
    const pageFromStorage = getStoredPage();
    if (pageFromStorage !== null) {
      return pageFromStorage;
    }
    return pageFromParamsInfo.page;
  });

  const { viewMode: paramsViewMode, hasOverride: hasViewOverride } =
    getViewModeFromParams(searchParams);
  const [viewMode, setViewMode] = useState(() => {
    if (hasViewOverride) {
      return paramsViewMode;
    }
    const viewFromStorage = getStoredViewMode();
    if (viewFromStorage) {
      return viewFromStorage;
    }
    return paramsViewMode;
  });

  const [engineUpdating, setEngineUpdating] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);
  const { filters: paramsFilters, hasOverrides: hasFilterOverrides } = getFiltersFromParams(searchParams);
  const [filters, setFilters] = useState(() => {
    if (hasFilterOverrides) {
      return paramsFilters;
    }
    const filtersFromStorage = getStoredFilters();
    if (filtersFromStorage) {
      return filtersFromStorage;
    }
    return paramsFilters;
  });
  const [testRuleDialogOpen, setTestRuleDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  const queryVariables = useMemo(
    () => {
      const variables: any = {
        ruleType: 'order_allocation',
        limit: 12,
        offset: page * 12,
      };

      if (filters.status !== 'all') {
        variables.isActive = filters.status === 'active';
      }

      if (filters.searchText && filters.searchText.trim()) {
        variables.search = filters.searchText.trim();
      }

      return variables;
    },
    [filters.status, filters.searchText, page]
  );

  const {
    data: rulesData,
    loading: rulesLoading,
    error: rulesError,
    refetch: refetchRules,
  } = useQuery(FETCH_ALLOCATION_RULES, {
    ...stagingOptions,
    variables: queryVariables,
    fetchPolicy: 'network-only',
  });

  const {
    data: engineStatusData,
    loading: engineStatusLoading,
    refetch: refetchEngineStatus,
  } = useQuery(FETCH_ALLOCATION_ENGINE_STATUS, {
    ...stagingOptions,
    fetchPolicy: 'cache-and-network',
  });

  const [toggleEngineMutation, { loading: toggleEngineLoading }] = useMutation(
    TOGGLE_ALLOCATION_ENGINE,
    stagingOptions
  );
  const [deleteRuleMutation] = useMutation(DELETE_ALLOCATION_RULE, stagingOptions);
  const [updateRuleMutation] = useMutation(UPDATE_ALLOCATION_RULE, stagingOptions);

  useEffect(() => {
    if ((location.state as any)?.refresh) {
      refetchRules();
      refetchEngineStatus();
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, refetchRules, refetchEngineStatus]);

  useEffect(() => {
    storage.set(STORAGE_KEYS.page, String(page));
  }, [page]);

  useEffect(() => {
    storage.set(STORAGE_KEYS.viewMode, viewMode);
  }, [viewMode]);

  useEffect(() => {
    storage.set(STORAGE_KEYS.filters, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    const currentString = searchParams.toString();
    if (currentString === lastSyncedSearchParamsRef.current) {
      return;
    }

    lastSyncedSearchParamsRef.current = currentString;

    const { page: nextPage } = getPageFromParams(searchParams);
    if (nextPage !== page) {
      setPage(nextPage);
    }

    const nextFilters = getFiltersFromParams(searchParams).filters;
    if (!areFiltersEqual(filters, nextFilters)) {
      setFilters(nextFilters);
    }

    const { viewMode: nextViewMode } = getViewModeFromParams(searchParams);
    if (nextViewMode !== viewMode) {
      setViewMode(nextViewMode);
    }
  }, [searchParams, page, filters, viewMode]);

  useEffect(() => {
    const currentString = searchParams.toString();
    if (currentString !== lastSyncedSearchParamsRef.current) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);

    if (page > 0) {
      nextParams.set('page', String(page + 1));
    } else {
      nextParams.delete('page');
    }

    if (viewMode !== DEFAULT_VIEW_MODE) {
      nextParams.set('view', viewMode);
    } else {
      nextParams.delete('view');
    }

    if (filters.status !== DEFAULT_FILTERS.status) {
      nextParams.set('status', filters.status);
    } else {
      nextParams.delete('status');
    }

    if (filters.resultVia && filters.resultVia !== DEFAULT_FILTERS.resultVia) {
      nextParams.set('resultVia', filters.resultVia);
    } else {
      nextParams.delete('resultVia');
    }

    if (filters.priority !== DEFAULT_FILTERS.priority) {
      nextParams.set('priority', filters.priority);
    } else {
      nextParams.delete('priority');
    }

    if (filters.searchText) {
      nextParams.set('searchText', filters.searchText);
    } else {
      nextParams.delete('searchText');
    }

    const nextString = nextParams.toString();
    if (nextString !== currentString) {
      lastSyncedSearchParamsRef.current = nextString;
      setSearchParams(nextParams, { replace: true });
    }
  }, [page, filters, viewMode, searchParams, setSearchParams]);

  const rules = rulesData?.fetchAllAllocationRules?.items || [];
  const engineStatus = engineStatusData?.fetchAllocationEngineStatus;
  const engineEnabled = engineStatus?.enabled ?? false;

  const totalRules = engineStatus?.totalRulesCount ?? 0;
  const activeRulesCount = engineStatus?.activeRulesCount ?? 0;
  const totalConditions = engineStatus?.totalConditions ?? 0;
  const averageConditions = engineStatus?.avgConditions ?? 0;
  const priorityDistribution = engineStatus?.priorityDistribution || { high: 0, medium: 0, low: 0 };
  const statusDistribution = engineStatus?.statusDistribution || {
    activeCount: 0,
    activePercentage: 0,
    inactiveCount: 0,
    inactivePercentage: 0,
  };

  const rulesByResultVia = useMemo(() => {
    const breakdown: Record<string, number> = {};
    rules.forEach((rule: any) => {
      breakdown[rule.resultVia] = (breakdown[rule.resultVia] || 0) + 1;
    });
    return breakdown;
  }, [rules]);

  const mostComplexRule = useMemo(() => {
    if (rules.length === 0) return null;
    return rules.reduce((max: any, rule: any) => {
      const maxConditions = (max.conditions || []).length;
      const ruleConditions = (rule.conditions || []).length;
      return ruleConditions > maxConditions ? rule : max;
    });
  }, [rules]);

  const filteredRules = useMemo(() => {
    return rules.filter((rule: any) => {
      if (filters.resultVia !== 'all' && rule.resultVia !== filters.resultVia) {
        return false;
      }

      if (filters.priority !== 'all') {
        if (filters.priority === 'high' && rule.priority < 15) return false;
        if (filters.priority === 'medium' && (rule.priority < 8 || rule.priority >= 15))
          return false;
        if (filters.priority === 'low' && rule.priority >= 8) return false;
      }

      return true;
    });
  }, [rules, filters]);

  const serverTotalCount = rulesData?.fetchAllAllocationRules?.totalCount ?? 0;

  const displayRules = filteredRules;


  const handleFilterChange = (filterName: keyof typeof filters, value: any) => {
    setFilters((prev: any) => ({
      ...prev,
      [filterName]: value,
    }));
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilters({
      status: 'all',
      resultVia: 'all',
      priority: 'all',
      searchText: '',
    });
  };

  const handleEdit = (rule: any) => {
    navigate(`/allocation/edit/${rule.id}`);
  };

  const handleViewRule = (rule: any) => {
    navigate(`/allocation/${rule.id}`);
  };

  const getResultViaVariant = (resultVia: string): BadgeVariant => {
    const mapping: Record<string, BadgeVariant> = {
      'Partner': 'partner',
      'Internal': 'internal',
      'Courier': 'courier',
      'InternalWarehouse': 'warehouse',
    };
    return mapping[resultVia] || 'default';
  };

  const getErrorMessage = (error: any, fallback: string): string =>
    error?.graphQLErrors?.[0]?.message || error?.message || fallback;

  const handleToggleEngine = async (nextValue: boolean) => {
    setEngineUpdating(true);
    try {
      await toggleEngineMutation({
        variables: { enabled: nextValue },
      });
      await refetchEngineStatus();
      toast.success(`Allocation engine ${nextValue ? 'enabled' : 'disabled'} successfully.`);
    } catch (error) {
      console.error('Failed to toggle engine', error);
      toast.error(getErrorMessage(error, 'Unable to update engine status.'));
    } finally {
      setEngineUpdating(false);
    }
  };

  const handleDelete = async (ruleId: string) => {
    const rule = rules.find((r: any) => r.id === ruleId);
    const ruleName = rule?.name || 'Unknown';

    if (!window.confirm(`Are you sure you want to delete the rule "${ruleName}"?`)) return;

    setDeletingRuleId(ruleId);
    try {
      await deleteRuleMutation({
        variables: { id: ruleId },
      });
      await refetchRules();

      toast.success(
        <div>
          <div className="font-semibold">Allocation rule deleted successfully!</div>
          <div className="text-sm mt-1">
            <strong>Name:</strong> {truncateToastText(ruleName, 12)} | <strong>ID:</strong> {ruleId}
          </div>
        </div>
      );
    } catch (error) {
      console.error('Failed to delete rule', error);
      toast.error(getErrorMessage(error, 'Unable to delete rule.'));
    } finally {
      setDeletingRuleId(null);
    }
  };

  const handleToggleRule = async (rule: any) => {
    try {
      await updateRuleMutation({
        variables: {
          input: {
            id: rule.id,
            isActive: !rule.isActive,
          },
        },
      });
      await refetchRules();
      await refetchEngineStatus();
      toast.success(`Rule "${rule.name}" ${!rule.isActive ? 'activated' : 'deactivated'} successfully.`);
    } catch (error) {
      console.error('Failed to toggle rule', error);
      toast.error(getErrorMessage(error, 'Unable to update rule status.'));
    }
  };

  const handleTest = (rule: any) => {
    setSelectedRule(rule);
    setTestRuleDialogOpen(true);
  };

  return (
    <div className="flex flex-col space-y-2 relative bg-white">
      <div className="main-wrapper">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="pageTitle mb-1 text-xl font-bold text-gray-900">Allocation Dashboard</h2>
            {engineStatusLoading ? (
              <p className="text-sm text-gray-400">Loading engine status...</p>
            ) : (
              <p className={`text-sm font-medium ${engineEnabled ? 'text-green-600' : 'text-red-600'}`}>
                {engineStatus?.statusMessage || (engineEnabled ? 'Internal allocation engine is active' : 'Internal allocation engine is inactive')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <CustomButton
              variant="outlined"
              color="primary"
              onClick={() => navigate('/allocation/instructions')}
              additionalStyles="!flex-none text-sm py-2.5 font-semibold shadow-md hover:shadow-lg transition-all duration-200 !min-w-0 px-4"
            >
              <div className="flex items-center justify-center gap-2 whitespace-nowrap">
                <DescriptionIcon fontSize="small" />
                <span>Instructions</span>
              </div>
            </CustomButton>
            <CustomButton
              variant="contained"
              color="primary"
              onClick={() => navigate('/allocation/create')}
              additionalStyles="!flex-none text-sm py-2.5 font-semibold shadow-lg hover:shadow-xl transition-all duration-200 !min-w-0 px-4"
            >
              <div className="flex items-center justify-center gap-2 whitespace-nowrap">
                <AddIcon fontSize="small" />
                <span>Create Rule</span>
              </div>
            </CustomButton>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap mb-6">
          {rulesLoading || engineStatusLoading ? (
            Array.from({ length: 7 }).map((_, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm px-4 py-3 flex items-center gap-3 min-w-[140px]">
                <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: '8px' }} />
                <div className="flex-1">
                  <Skeleton variant="text" width={80} height={14} />
                  <Skeleton variant="text" width={50} height={24} className="mt-1" />
                </div>
              </div>
            ))
          ) : (
            <>
              <div className={`bg-gradient-to-br from-white ${engineEnabled ? 'to-green-50/30' : 'to-red-50/30'} rounded-xl border ${engineEnabled ? 'border-green-200/60' : 'border-red-200/60'} shadow-sm hover:shadow-md px-4 py-3 flex items-center justify-between gap-3 min-w-[180px] transition-all`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${engineEnabled ? 'from-green-100 to-green-50' : 'from-red-100 to-red-50'} flex items-center justify-center shadow-sm`}>
                    <div className={`w-3 h-3 rounded-full ${engineEnabled ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium">Engine Status</p>
                    <p className={`text-xl font-bold ${engineEnabled ? 'text-green-600' : 'text-red-600'}`}>
                      {engineEnabled ? 'ON' : 'OFF'}
                    </p>
                  </div>
                </div>
                <Switch
                  label=""
                  checked={engineEnabled}
                  onCheckedChange={handleToggleEngine}
                  disabled={toggleEngineLoading || engineUpdating}
                />
              </div>

              <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shadow-sm">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium">Total Rules</p>
                  <p className="text-xl font-bold text-gray-900">{totalRules}</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[160px] transition-all">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center shadow-sm">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-600 font-medium">Active</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-xl font-bold text-gray-900">{activeRulesCount}</p>
                    <p className="text-sm font-semibold text-green-600">({statusDistribution.activePercentage.toFixed(1)}%)</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[160px] transition-all">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shadow-sm">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-600 font-medium">Inactive</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-xl font-bold text-gray-900">{statusDistribution.inactiveCount}</p>
                    <p className="text-sm font-semibold text-gray-600">({statusDistribution.inactivePercentage.toFixed(1)}%)</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-orange-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[180px] transition-all">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center shadow-sm">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-600 font-medium">Conditions</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-xl font-bold text-gray-900">{totalConditions}</p>
                    <p className="text-sm font-semibold text-orange-600">(avg: {averageConditions.toFixed(1)})</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-purple-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 min-w-[240px] transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center shadow-sm flex-shrink-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#9333EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-600 font-medium mb-1">Priority</p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">High:</span>
                        <span className="text-base font-bold text-red-600">{priorityDistribution.high}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">Medium:</span>
                        <span className="text-base font-bold text-yellow-600">{priorityDistribution.medium}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">Low:</span>
                        <span className="text-base font-bold text-blue-600">{priorityDistribution.low}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="main-wrapper">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-base font-bold text-gray-900">All Allocation Rules</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Manage your allocation rules to automatically route orders to the appropriate channels
              </p>
            </div>
            <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 shadow-inner" role="group">
              <button
                type="button"
                onClick={() => {
                  console.log('Card view button clicked, current mode:', viewMode);
                  setViewMode('card');
                }}
                className={`relative px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${viewMode === 'card'
                  ? 'bg-white text-primaryColor shadow-md transform scale-[1.02]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                title="Card View"
                aria-pressed={viewMode === 'card'}
              >
                <GridViewIcon fontSize="small" />
                <span className="hidden sm:inline">Card</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  console.log('Table view button clicked, current mode:', viewMode);
                  setViewMode('table');
                }}
                className={`relative px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${viewMode === 'table'
                  ? 'bg-white text-primaryColor shadow-md transform scale-[1.02]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                title="Table View"
                aria-pressed={viewMode === 'table'}
              >
                <ListViewIcon fontSize="small" />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>
          </div>

          <AllocationFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
          />

          {rulesLoading ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12">
              <div className="flex flex-col items-center justify-center gap-4">
                <CircularProgress size={40} sx={{ color: '#0f62fe' }} />
                <p className="text-base font-medium text-gray-700">Loading allocation rules...</p>
              </div>
            </div>
          ) : rulesError ? (
            <div className="bg-white rounded-xl border border-red-200 p-12 text-center text-red-600 space-y-4">
              <p>Failed to load allocation rules. Please try again.</p>
              <CustomButton
                variant="outlined"
                color="primary"
                onClick={() => refetchRules()}
                additionalStyles="text-base"
              >
                Retry
              </CustomButton>
            </div>
          ) : rules.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <p className="text-base text-gray-600 mb-4">No rules configured yet.</p>
              <CustomButton
                variant="contained"
                color="primary"
                onClick={() => navigate('/allocation/create')}
                additionalStyles="text-base"
              >
                Create Your First Rule
              </CustomButton>
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <p className="text-base text-gray-600 mb-4">No rules match the current filters.</p>
              <CustomButton
                variant="outlined"
                color="primary"
                onClick={handleResetFilters}
                additionalStyles="text-base"
              >
                Reset Filters
              </CustomButton>
            </div>
          ) : (
            <>
              {viewMode === 'card' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {displayRules.map((rule: any) => (
                    <RuleCard
                      key={rule.id}
                      rule={rule}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onTest={handleTest}
                      onView={handleViewRule}
                      onToggle={handleToggleRule}
                      isDeleting={deletingRuleId === rule.id}
                    />
                  ))}
                </div>
              ) : (
                <div className="w-full overflow-hidden">
                  <TableContainer className="overflow-x-hidden">
                    <Table
                      size="small"
                      stickyHeader
                      aria-label="rules table"
                      sx={{
                        width: '100%',
                        tableLayout: 'fixed',
                        borderCollapse: 'separate',
                        borderSpacing: '0 10px',
                        '& thead th': {
                          borderBottom: 'none',
                          fontSize: '12px',
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          backgroundColor: 'rgba(249, 250, 251, 0.8)',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow className='bg-gray-400 rounded-lg'>
                          <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '25%' }}>Rule Name</TableCell>
                          <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%' }}>Priority</TableCell>
                          <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%' }}>Result Via</TableCell>
                          <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '12%' }}>Result Data</TableCell>
                          <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '15%' }}>Description</TableCell>
                          <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%' }}>Created</TableCell>
                          <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%' }}>Updated</TableCell>
                          <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '10%' }}>Status</TableCell>
                          <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" align="right" sx={{ width: '12%' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayRules.map((rule: any) => {
                          return (
                            <TableRow
                              key={rule.id}
                              sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                '& td, & th': { borderBottom: 'none' },
                                '& td:first-of-type, & th:first-of-type': {
                                  borderTopLeftRadius: 12,
                                  borderBottomLeftRadius: 12,
                                },
                                '& td:last-of-type, & th:last-of-type': {
                                  borderTopRightRadius: 12,
                                  borderBottomRightRadius: 12,
                                },
                                '&:hover': {
                                  background: 'linear-gradient(90deg, rgba(75, 85, 99, 0.15) 0%, rgba(75, 85, 99, 0.08) 100%)',
                                  boxShadow: '0 4px 16px rgba(75, 85, 99, 0.25)',
                                  '& td:first-of-type, & th:first-of-type': {
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                  },
                                  '& td:last-of-type, & th:last-of-type': {
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                  },
                                },
                                transition: 'all 0.2s ease-in-out',
                                position: 'relative',
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 24,
                                  right: 24,
                                  bottom: -5,
                                  height: '1px',
                                  backgroundColor: 'rgba(226, 232, 240, 0.9)',
                                }
                              }}
                              className="bg-white border border-gray-100"
                            >
                              <TableCell component="th" scope="row" className="py-2.5" sx={{ overflow: 'hidden' }}>
                                <div className="flex flex-col leading-tight min-w-0">
                                  <span className="text-[11px] font-mono text-gray-500 uppercase tracking-wide">
                                    #{rule.id}
                                  </span>
                                  {shouldShowTooltip(rule.name) ? (
                                    <Tooltip
                                      title={rule.name}
                                      arrow
                                      placement="top"
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: 'white',
                                            color: 'rgba(0, 0, 0, 0.87)',
                                            fontSize: '0.75rem',
                                            maxWidth: '200px',
                                            padding: '8px 12px',
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                          }
                                        },
                                        arrow: {
                                          sx: {
                                            color: 'white',
                                          }
                                        }
                                      }}
                                    >
                                      <span className="font-semibold text-sm text-gray-900 mt-0.5 cursor-help truncate block min-w-0">
                                        {rule.name}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <span className="font-semibold text-sm text-gray-900 mt-0.5 truncate block min-w-0">
                                      {rule.name}
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="py-2.5">
                                <span className="text-sm font-bold text-blue-600 px-2 py-0.5 rounded-md bg-blue-50">
                                  {rule.priority}
                                </span>
                              </TableCell>
                              <TableCell className="py-2.5">
                                <Badge variant={getResultViaVariant(rule.resultVia)} className="text-[11px] font-semibold px-2 py-0.5">
                                  {rule.resultVia}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-2.5" sx={{ overflow: 'hidden' }}>
                                {rule.resultData && shouldShowTooltip(rule.resultData) ? (
                                  <Tooltip
                                    title={rule.resultData}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: 'white',
                                          color: 'rgba(0, 0, 0, 0.87)',
                                          fontSize: '0.75rem',
                                          maxWidth: '200px',
                                          padding: '8px 12px',
                                          border: '1px solid rgba(0, 0, 0, 0.12)',
                                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                        }
                                      },
                                      arrow: {
                                        sx: {
                                          color: 'white',
                                        }
                                      }
                                    }}
                                  >
                                    <span className="text-sm text-gray-800 font-medium truncate block cursor-help min-w-0">
                                      {rule.resultData}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <span className="text-sm text-gray-800 font-medium truncate block min-w-0">
                                    {rule.resultData || '—'}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="py-2.5" sx={{ overflow: 'hidden' }}>
                                {rule.description && shouldShowTooltip(rule.description) ? (
                                  <Tooltip
                                    title={rule.description}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: 'white',
                                          color: 'rgba(0, 0, 0, 0.87)',
                                          fontSize: '0.75rem',
                                          maxWidth: '200px',
                                          padding: '8px 12px',
                                          border: '1px solid rgba(0, 0, 0, 0.12)',
                                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                        }
                                      },
                                      arrow: {
                                        sx: {
                                          color: 'white',
                                        }
                                      }
                                    }}
                                  >
                                    <span className="text-sm text-gray-600 truncate cursor-help block min-w-0">
                                      {rule.description}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <span className="text-sm text-gray-600 truncate block min-w-0">
                                    {rule.description || '—'}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="py-2.5">
                                <div className="flex flex-col">
                                  <span className="text-[11px] text-gray-700 font-semibold">
                                    {rule.createdAt ? moment(rule.createdAt).format('DD/MM/YYYY') : '—'}
                                  </span>
                                  <span className="text-[10px] text-gray-500">
                                    {rule.createdAt ? moment(rule.createdAt).format('HH:mm') : ''}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="py-2.5">
                                <div className="flex flex-col">
                                  <span className="text-[11px] text-gray-700 font-semibold">
                                    {rule.updatedAt ? moment(rule.updatedAt).format('DD/MM/YYYY') : '—'}
                                  </span>
                                  <span className="text-[10px] text-gray-500">
                                    {rule.updatedAt ? moment(rule.updatedAt).format('HH:mm') : ''}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="py-2.5">
                                <FormControlLabel
                                  control={
                                    <MUISwitch
                                      checked={rule.isActive}
                                      onChange={() => handleToggleRule(rule)}
                                      color="primary"
                                      size="small"
                                      disabled={engineUpdating}
                                      sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: '#10B981',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: '#10B981',
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <span className="text-xs font-medium text-gray-700">
                                      {rule.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                  }
                                  sx={{
                                    margin: 0,
                                    '& .MuiFormControlLabel-label': {
                                      marginLeft: '8px',
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ pl: 3 }}
                              >
                                {deletingRuleId === rule.id ? (
                                  <div className="flex items-center justify-end gap-1.5 text-[11px] text-gray-500 pr-2">
                                    <CircularProgress size={14} sx={{ color: '#6B7280' }} />
                                    <span className="font-medium">Deleting…</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-end gap-1.5 pr-1">
                                    <Tooltip title="View details">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewRule(rule);
                                          }}
                                          className="text-gray-500 hover:text-primaryColor hover:bg-gray-100 transition-colors"
                                          disabled={engineUpdating}
                                          sx={{
                                            padding: '4px',
                                            '&:hover': {
                                              backgroundColor: 'rgba(59, 130, 246, 0.06)',
                                            }
                                          }}
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Test rule">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTest(rule);
                                          }}
                                          className="text-gray-500 hover:text-green-600 hover:bg-gray-100 transition-colors"
                                          disabled={engineUpdating}
                                          sx={{
                                            padding: '4px',
                                            '&:hover': {
                                              backgroundColor: 'rgba(16, 185, 129, 0.06)',
                                            }
                                          }}
                                        >
                                          <PlayArrowIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEdit(rule);
                                          }}
                                          className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                          disabled={engineUpdating}
                                          sx={{
                                            padding: '4px',
                                            '&:hover': {
                                              backgroundColor: 'rgba(55, 65, 81, 0.06)',
                                            }
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(rule.id);
                                          }}
                                          className="text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                          disabled={engineUpdating}
                                          sx={{
                                            padding: '4px',
                                            '&:hover': {
                                              backgroundColor: 'rgba(248, 113, 113, 0.08)',
                                            }
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}

              {serverTotalCount > 0 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  {rulesLoading ? (
                    <div className="flex items-center gap-2 text-base text-gray-600">
                      <CircularProgress size={16} sx={{ color: '#6B7280' }} />
                      <span className="text-gray-500">Loading...</span>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-gray-600">
                        Showing {page * 12 + 1} to {Math.min((page + 1) * 12, serverTotalCount)} of {serverTotalCount} rule{serverTotalCount !== 1 ? 's' : ''}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setPage(Math.max(0, page - 1))}
                          disabled={page === 0}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page === 0
                            ? 'bg-white text-gray-400 cursor-not-allowed border border-gray-200'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                        >
                          Previous
                        </button>
                        {Array.from({ length: Math.ceil(serverTotalCount / 12) }, (_, i) => i).map((pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page === pageNum
                              ? 'bg-primaryColor text-white border border-primaryColor'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                              }`}
                          >
                            {pageNum + 1}
                          </button>
                        ))}
                        <button
                          onClick={() => setPage(Math.min(Math.ceil(serverTotalCount / 12) - 1, page + 1))}
                          disabled={page >= Math.ceil(serverTotalCount / 12) - 1}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page >= Math.ceil(serverTotalCount / 12) - 1
                            ? 'bg-white text-gray-400 cursor-not-allowed border border-gray-200'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                        >
                          Next
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <TestRuleDialog
          open={testRuleDialogOpen}
          onOpenChange={setTestRuleDialogOpen}
          rule={selectedRule}
        />
      </div>
    </div>
  );
};

export default Dashboard;


